import { ICellRendererParams } from 'ag-grid-community';
import { TokenBalance } from '../../types';
import { weiToEthBigInt, safeConvertToNumber } from 'src/utils/number';

// Pre-create formatters for performance (expensive to create repeatedly)
const highPrecisionFlowFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 8,
});

const lowPrecisionFlowFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

interface TokenFlowCellRendererParams {
  flowType: 'incoming' | 'outgoing';
}

export const TokenFlowCellRenderer = (
  params: ICellRendererParams<TokenBalance> & TokenFlowCellRendererParams,
) => {
  const token = params.data;
  const flowType = params.flowType;

  if (!token || !flowType) {
    return <span className="text-muted-foreground">-</span>;
  }

  const value =
    flowType === 'incoming' ? token.total_incoming : token.total_outgoing;

  if (value === null || value === undefined || value === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  // For ETH tokens, use Wei conversion (handles MongoDB Long automatically)
  let actualAmount: number;

  if (token.symbol === 'ETH' || token.decimals === 18) {
    // Convert from Wei to ETH using the utility (handles MongoDB Long)
    const ethAmount = weiToEthBigInt(value);
    actualAmount = parseFloat(ethAmount);
  } else {
    // For other tokens, safely convert to number first (handles MongoDB Long)
    const numericValue = safeConvertToNumber(value);

    // Check if decimals is defined
    if (token.decimals === undefined || token.decimals === null) {
      // If decimals is undefined/null, use the converted numeric value
      actualAmount = numericValue;
    } else {
      // Use the decimals field for conversion
      actualAmount = numericValue / Math.pow(10, token.decimals);
    }
  }

  // Handle NaN case
  if (isNaN(actualAmount)) {
    return <span className="text-muted-foreground">-</span>;
  }

  // Use pre-created formatters for better performance
  const formatter =
    actualAmount < 1 ? highPrecisionFlowFormatter : lowPrecisionFlowFormatter;
  const formattedAmount = formatter.format(actualAmount);

  const colorClass =
    flowType === 'incoming'
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400';

  return (
    <div className="flex flex-col">
      <span className={`font-mono ${colorClass}`}>{formattedAmount}</span>
    </div>
  );
};
